import React, { useEffect, useState } from "react";
import {
  ArrowDownIcon,
  HomeIcon,
  RentReceiptIcon,
  UserRoundIcon,
} from "../../../../icons/rent-receipt-icons";
import RentReceiptPersonalDetails from "./personal-details";
import RentDetails from "./rent-details";
import { SubmitHandler, useForm } from "react-hook-form";
import { useGenerateRentReceiptMutation } from "../../../../store/api/toolsApi";
import { toastError } from "../../../toast";
import { BtnLoader } from "../../../../icons";
import { Receipt } from "./receipt";

export interface RentReceiptForm {
  first_name: string;
  last_name: string;
  email: string;
  pan?: string;
  monthlyRent: number;
  startDate: string;
  endDate: string;
  ownerName: string;
  ownerPan?: string;
  address: string;
}

interface ReceiptResponse {
  content: string;
  owner: {
    name: string;
    pan?: string;
  };
}

const RentReceipt = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isEditing, setIsEditing] = useState(true);
  const [receiptData, setreceiptData] = useState<ReceiptResponse | null>(null);
  const handleNext = () => {
    setCurrentStep(Math.min(steps.length, currentStep + 1));
  };

  const handlePrevious = () => {
    setCurrentStep(Math.max(1, currentStep - 1));
  };

  const steps = [
    {
      number: 1,
      title: "Personal Details",
      duration: "(2min)",
      description:
        "This detail help us to personalize your rent receipt and ensure it meets legal requirements.",
      icon: () => <UserRoundIcon />,
    },
    {
      number: 2,
      title: "Home Profile",
      duration: "(2min)",
      description:
        "This detail ensures accuracy for tax and documentation purposes.",
      icon: (isActive: boolean) => (
        <HomeIcon fillColor={!isActive ? "gray" : "#ECD996"} />
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RentReceiptForm>({ mode: "all" });
  const [generateReceipt, { isLoading, isError }] =
    useGenerateRentReceiptMutation();

  const onSubmit: SubmitHandler<RentReceiptForm> = async (data) => {
    try {
      setIsEditing(false);
      const response = await generateReceipt({
        first_name: data.first_name,
        last_name: data.last_name,
        monthly_rent: data.monthlyRent,
        start_date: new Date(data.startDate).toISOString(),
        end_date: new Date(data.endDate).toISOString(),
        pan: data.pan,
        email: data.email,
        rent_details: {
          owner_name: data.ownerName,
          owner_pan: data.ownerPan,
          address: data.address,
        },
      }).unwrap();
      setreceiptData(response?.data?.receipt_details);
    } catch (error) {
      console.log(error);
      toastError("Something went wrong.");
    }
  };

  useEffect(() => {
    if (isError) {
      setIsEditing(true);
      toastError("Something went wrong.");
    }
  }, [isError]);
  return (
    <>
      <div className="px-[24px] py-[16px] text-[32px] font-bold leading-[1.06] border-b border-[#e1e1e1]">
        Rent Receipt Generator
      </div>
      {isEditing ? (
        <div className="flex w-full bg-black">
          <div className="bg-black w-full max-w-[380px] flex items-center p-6 flex-col justify-between">
            <div className="max-w-md w-full mt-[20px]">
              {steps.map((step, index) => (
                <div key={step.number} className="flex">
                  <div className="flex flex-col items-center">
                    <div>{step.icon(currentStep >= step.number)}</div>

                    {index < steps.length - 1 && (
                      <div className="text-gray-500 relative top-4">
                        <ArrowDownIcon
                          fillColor={
                            !(currentStep > step.number) ? "#BDBDBD" : "#ecd996"
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="ml-4 pb-12">
                    <div
                      className={`uppercase font-medium tracking-wider text-sm mb-1 text-[16px] ${
                        currentStep >= step.number
                          ? "text-[#e6d58d]"
                          : "text-gray-600"
                      }`}
                    >
                      STEP {step.number}
                    </div>

                    <h3
                      className={`text-[20px] leading-1.3 font-bold ${currentStep >= step.number ? "text-white" : "text-gray-600"}`}
                    >
                      {step.title}
                    </h3>

                    <div
                      className={`text-[16px] leading-1.3 font-bold ${currentStep >= step.number ? "text-white" : "text-gray-600"}`}
                    >
                      {step.duration}
                    </div>

                    <p
                      className={`text-[13px]] ${currentStep >= step.number ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-white mt-[50px] flex justify-center items-stretch gap-4 p-[8px] rounded-[10px] border border-[#404040] bg-[#262626]">
              <div className="flex items-center">
                <RentReceiptIcon />
              </div>
              <div className="text-[14px] ml-[10px] mr-[15px]">
                Generate rent receipt for submitting to your employer for
                claiming HRA deduction
              </div>
            </div>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full text-black bg-white flex flex-col justify-between"
          >
            {currentStep === 1 ? (
              <RentReceiptPersonalDetails
                errors={errors}
                register={register}
                watch={watch}
              />
            ) : (
              <RentDetails register={register} watch={watch} />
            )}

            <div className="border-t border-[#e1e1e1] px-[24px] py-[16px] flex flex-col w-full items-end">
              <div className="flex justify-center items-center">
                <div
                  className="px-[48px] py-[8px] rounded-[12px] text-[15px] font-bold bg-[#ebebeb] text-black cursor-pointer"
                  onClick={handlePrevious}
                >
                  Back
                </div>
                {currentStep === 1 ? (
                  <div
                    className="ml-[16px] px-[48px] py-[8px] rounded-[12px] text-[15px] font-bold bg-[#007aff] text-white cursor-pointer"
                    onClick={handleNext}
                  >
                    {"Next"}
                  </div>
                ) : (
                  <input
                    type="submit"
                    className="ml-[16px] px-[48px] py-[8px] rounded-[12px] text-[15px] font-bold bg-[#007aff] text-white cursor-pointer"
                  />
                )}
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex items-center w-full h-full p-6 justify-center">
          {isLoading ? (
            <BtnLoader />
          ) : (
            <Receipt
              ownerName={receiptData?.owner?.name as string}
              ownerPan={receiptData?.owner?.pan as string}
              content={receiptData?.content as string}
            />
          )}
        </div>
      )}
    </>
  );
};

export default RentReceipt;
